import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, MessageSquare, Send, Loader2, Check, CheckCheck,
  AlertCircle, User, ChevronDown, FileText, Zap, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Available tokens that can be inserted into messages
const AVAILABLE_TOKENS = [
  { token: '{customer_name}', label: 'First Name', description: 'Customer first name' },
  { token: '{full_name}', label: 'Full Name', description: 'Customer full name' },
  { token: '{service}', label: 'Service', description: 'Service type' },
  { token: '{amount}', label: 'Amount', description: 'Job/invoice amount' },
  { token: '{balance}', label: 'Balance', description: 'Outstanding balance' },
  { token: '{date}', label: 'Date', description: 'Scheduled date' },
  { token: '{time}', label: 'Time', description: 'Scheduled time' },
  { token: '{address}', label: 'Address', description: 'Customer address' },
  { token: '{business_name}', label: 'Business', description: 'Your company name' },
  { token: '{payment_link}', label: 'Pay Link', description: 'Online payment link' },
];

// Default SMS templates with placeholders
const DEFAULT_TEMPLATES = [
  {
    id: 'reminder',
    name: 'Appointment Reminder',
    message: 'Hi {customer_name}, just a reminder that your {service} is scheduled for tomorrow. Thanks, {business_name}'
  },
  {
    id: 'on_way',
    name: 'On My Way',
    message: 'Hi {customer_name}, I\'m on my way to you now. Should be there in about 15 minutes. {business_name}'
  },
  {
    id: 'completed',
    name: 'Job Completed',
    message: 'Hi {customer_name}, your {service} has been completed. Total: £{amount}. Thanks for choosing {business_name}!'
  },
  {
    id: 'payment_due',
    name: 'Payment Reminder',
    message: 'Hi {customer_name}, friendly reminder that you have an outstanding balance of £{balance}. Pay online: {payment_link} - {business_name}'
  },
  {
    id: 'reschedule',
    name: 'Job Rescheduled',
    message: 'Hi {customer_name}, your {service} has been rescheduled to {date}. Let us know if this doesn\'t work. {business_name}'
  },
  {
    id: 'custom',
    name: 'Custom Message',
    message: ''
  }
];

const SMSComposeModal = ({ 
  isOpen, 
  onClose, 
  recipients = [], // Array of { id, name, phone, customer_id }
  defaultTemplate = null,
  context = {} // { service, amount, date, balance, etc. }
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate || 'custom');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [smsSettings, setSmsSettings] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [creditsNeeded, setCreditsNeeded] = useState(0);
  const [showTokens, setShowTokens] = useState(false);
  const textareaRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Insert token at cursor position
  const insertToken = (token) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = message.substring(0, start) + token + message.substring(end);
    
    setMessage(newMessage);
    
    // Set cursor position after the inserted token
    setTimeout(() => {
      const newPos = start + token.length;
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  // Fetch ClickSend sender status on mount.
  // We previously fetched `/api/sms/settings` (legacy Twilio fields) which
  // is why "From: Not set" appeared even after the user verified their
  // ClickSend Own Number — that endpoint reads `sms_sender_id` while the
  // actual sender lives on `clicksend_sender_id`.
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API}/api/clicksend/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSmsSettings({
            sms_enabled: !!data.configured,
            sms_sender_id: data.sender_id || '',
            sms_sender_type: data.sender_type || '',
            sms_credits: 99999, // ClickSend enforces credits server-side; UI hint only
          });
        }
      } catch (error) {
        console.error('Error fetching ClickSend status:', error);
      }
    };
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  // Calculate credits needed when message or recipients change
  useEffect(() => {
    if (message) {
      // GSM-7: 160 chars = 1 credit, Unicode: 70 chars = 1 credit
      const isGSM = /^[\x00-\x7F]*$/.test(message);
      const charsPerCredit = isGSM ? 160 : 70;
      const creditsPerMessage = Math.ceil(message.length / charsPerCredit);
      setCreditsNeeded(creditsPerMessage * recipients.length);
    } else {
      setCreditsNeeded(0);
    }
  }, [message, recipients.length]);

  // Replace placeholders with actual values
  const replacePlaceholders = useCallback((template, recipient) => {
    let result = template;
    
    // Get business name from stored user data
    const storedUser = localStorage.getItem('streakfree_user');
    const businessName = storedUser ? JSON.parse(storedUser).company_name || 'Your Business' : 'Your Business';
    
    const replacements = {
      '{customer_name}': recipient.name?.split(' ')[0] || 'there',
      '{full_name}': recipient.name || 'Customer',
      '{service}': context.service || 'service',
      '{amount}': context.amount?.toFixed(2) || '0.00',
      '{balance}': context.balance?.toFixed(2) || '0.00',
      '{date}': context.date || 'soon',
      '{new_date}': context.newDate || context.date || 'soon',
      '{time}': context.time || '',
      '{address}': recipient.address || context.address || '',
      '{business_name}': businessName,
      '{payment_link}': context.paymentLink || ''
    };
    
    Object.entries(replacements).forEach(([key, value]) => {
      result = result.replace(new RegExp(key, 'g'), value);
    });
    
    return result;
  }, [context]);

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = DEFAULT_TEMPLATES.find(t => t.id === templateId);
    if (template && template.message) {
      // Replace placeholders with first recipient's data for preview
      const previewRecipient = recipients[0] || { name: 'Customer' };
      setMessage(replacePlaceholders(template.message, previewRecipient));
    }
    setShowTemplateDropdown(false);
  };

  // Send SMS to all recipients
  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    if (!smsSettings?.sms_enabled) {
      toast.error('SMS is not enabled. Please configure in Settings.');
      return;
    }
    
    if (!smsSettings?.sms_sender_id) {
      toast.error('Please set a Sender ID in SMS Settings');
      return;
    }
    
    if ((smsSettings?.sms_credits || 0) < creditsNeeded) {
      toast.error(`Insufficient credits. Need ${creditsNeeded}, have ${smsSettings?.sms_credits || 0}`);
      return;
    }
    
    setSending(true);
    const results = { success: 0, failed: 0, lastError: null };
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      for (const recipient of recipients) {
        if (!recipient.phone) {
          results.failed++;
          continue;
        }
        
        // Personalize message for each recipient
        const personalizedMessage = replacePlaceholders(message, recipient);
        
        try {
          // Use ClickSend send endpoint (per-recipient single send).
          // Legacy `/api/sms/send` (Twilio) is left in place for any older
          // flows but the modal now exclusively talks to ClickSend so the
          // sender ID matches what the user verified in Settings → SMS.
          const response = await fetch(`${API}/api/clicksend/send`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              to: recipient.phone,
              body: personalizedMessage,
              customer_id: recipient.customer_id || recipient.id
            })
          });
          
          if (response.ok) {
            results.success++;
          } else {
            // Try to surface ClickSend's specific error to the user.
            try {
              const err = await response.json();
              if (err && err.detail) results.lastError = err.detail;
            } catch (_parseErr) {
              // ignore non-JSON error bodies
            }
            results.failed++;
          }
        } catch (err) {
          results.failed++;
        }
      }
      
      if (results.success > 0) {
        toast.success(`Sent ${results.success} SMS message${results.success > 1 ? 's' : ''}${results.failed > 0 ? ` (${results.failed} failed)` : ''}`);
        onClose(true); // Close with success flag
      } else {
        // Surface the ClickSend error directly so the user knows why
        // (e.g. "Sender not approved", "Insufficient balance") rather
        // than a generic "Failed to send messages".
        toast.error(results.lastError || 'Failed to send messages');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error('Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const selectedTemplateName = DEFAULT_TEMPLATES.find(t => t.id === selectedTemplate)?.name || 'Custom Message';
  const hasInsufficientCredits = (smsSettings?.sms_credits || 0) < creditsNeeded;

  return (
    <div className="fixed inset-0 bg-black/50 z-[130] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div 
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl modal-shell-h flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquare size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-zinc-900">Send SMS</h2>
              <p className="text-xs text-zinc-500">
                {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Recipients Preview */}
          {recipients.length > 0 && recipients.length <= 5 && (
            <div className="flex flex-wrap gap-2">
              {recipients.map((r, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 rounded-full text-sm">
                  <User size={14} className="text-zinc-500" />
                  <span className="text-zinc-700">{r.name?.split(' ')[0] || 'Customer'}</span>
                  {r.phone && <span className="text-zinc-400 text-xs">{r.phone.slice(-4)}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Template Selector */}
          <div className="relative">
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Template
            </label>
            <button
              onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-left hover:bg-zinc-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-zinc-400" />
                <span className="text-zinc-900">{selectedTemplateName}</span>
              </div>
              <ChevronDown size={18} className={`text-zinc-400 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showTemplateDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                {DEFAULT_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-zinc-50 transition-colors flex items-center gap-2 ${
                      selectedTemplate === template.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    {template.id === 'custom' ? (
                      <Zap size={16} className="text-amber-500" />
                    ) : (
                      <FileText size={16} className="text-zinc-400" />
                    )}
                    <span className={selectedTemplate === template.id ? 'text-blue-600 font-medium' : 'text-zinc-700'}>
                      {template.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Message Input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-zinc-700">
                Message
              </label>
              <button
                type="button"
                onClick={() => setShowTokens(!showTokens)}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                  showTokens 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                <Plus size={12} />
                Insert Token
              </button>
            </div>
            
            {/* Clickable Tokens Panel */}
            {showTokens && (
              <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-xs text-blue-700 mb-2 font-medium">Click to insert at cursor:</p>
                <div className="flex flex-wrap gap-1.5">
                  {AVAILABLE_TOKENS.map(({ token, label }) => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => insertToken(token)}
                      className="px-2.5 py-1.5 bg-white border border-blue-200 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-colors shadow-sm"
                      title={token}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onSelect={(e) => setCursorPosition(e.target.selectionStart)}
              placeholder="Type your message or click 'Insert Token' to add personalization..."
              rows={4}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="sms-message-input"
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-zinc-500">
                {message.length} characters • {creditsNeeded} credit{creditsNeeded !== 1 ? 's' : ''} needed
              </span>
              {message.length > 160 && (
                <span className="text-xs text-amber-600">
                  Multi-part SMS ({Math.ceil(message.length / 153)} parts)
                </span>
              )}
            </div>
          </div>

          {/* Credits Warning */}
          {smsSettings && hasInsufficientCredits && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Insufficient Credits</p>
                <p className="text-xs text-red-600">
                  You need {creditsNeeded} credits but only have {smsSettings.sms_credits || 0}. 
                  Please top up in Settings → SMS Credits.
                </p>
              </div>
            </div>
          )}

          {/* SMS Not Enabled Warning */}
          {smsSettings && !smsSettings.sms_enabled && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">SMS Not Enabled</p>
                <p className="text-xs text-amber-600">
                  Please enable SMS messaging in Settings → SMS Credits.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-200 bg-zinc-50 rounded-b-2xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <span>From: <strong>{smsSettings?.sms_sender_id || 'Not set'}</strong></span>
              {smsSettings?.sms_sender_type === 'own_number' && (
                <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Verified</span>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-zinc-200 text-zinc-700 rounded-xl font-medium hover:bg-zinc-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !message.trim() || hasInsufficientCredits || !smsSettings?.sms_enabled}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-sms-btn"
            >
              {sending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send SMS
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Message Status Component - for use in message lists
export const MessageStatus = ({ status }) => {
  switch (status) {
    case 'sent':
      return (
        <span className="inline-flex items-center text-zinc-400" title="Sent">
          <Check size={14} />
        </span>
      );
    case 'delivered':
      return (
        <span className="inline-flex items-center text-blue-500" title="Delivered">
          <CheckCheck size={14} />
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center text-red-500" title="Failed">
          <AlertCircle size={14} />
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center text-zinc-300" title="Pending">
          <Loader2 size={14} className="animate-spin" />
        </span>
      );
    default:
      return null;
  }
};

export default SMSComposeModal;
